import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useNavigate } from 'react-router-dom';
import { popupsService } from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PopupsTable = () => {
  const navigate = useNavigate();
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    is_active: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState(null);

  useEffect(() => {
    loadPopups();
  }, [pagination.page, pagination.limit, filters]);

  const loadPopups = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const response = await popupsService.getAllPopups(params);

      if (response.success) {
        setPopups(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Popuplar yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading popups:', error);
      toast.error('Popuplar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPopup) return;

    try {
      const response = await popupsService.deletePopup(selectedPopup.id);
      if (response.success) {
        toast.success('Popup silindi');
        setShowDeleteModal(false);
        setSelectedPopup(null);
        loadPopups();
      } else {
        toast.error(response.error || 'Popup silinemedi');
      }
    } catch (error) {
      console.error('Error deleting popup:', error);
      toast.error('Popup silinirken bir hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getContentTypeLabel = (type) => {
    const labels = {
      image: 'Görsel',
      content: 'İçerik',
      video: 'Video'
    };
    return labels[type] || type;
  };

  return (
    <div>
      {/* Header with Add Button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Popup ara..."
            value={filters.search}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, search: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ width: '300px' }}
          />
          <select
            className="form-select"
            value={filters.is_active}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, is_active: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ width: '150px' }}
          >
            <option value="">Tüm Durumlar</option>
            <option value="true">Aktif</option>
            <option value="false">Pasif</option>
          </select>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/popups/add')}
        >
          <Icon icon="mdi:plus" className="me-1" />
          Popup Ekle
        </button>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
                  <th>ID</th>
              <th>Başlık</th>
              <th>İçerik Tipi</th>
              <th>Başlangıç Tarihi</th>
              <th>Bitiş Tarihi</th>
              <th>Filtre Sayısı</th>
              <th>İstatistikler</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </td>
              </tr>
            ) : popups.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center">Popup bulunamadı</td>
              </tr>
            ) : (
              popups.map((popup) => (
                <tr key={popup.id}>
                  <td>{popup.id}</td>
                  <td>{popup.title}</td>
                  <td>{getContentTypeLabel(popup.content_type)}</td>
                  <td>{formatDate(popup.start_date)}</td>
                  <td>{formatDate(popup.end_date)}</td>
                  <td>
                    <span className="badge bg-info">{popup.filter_count || 0}</span>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1" style={{ fontSize: '0.85rem' }}>
                      <div>
                        <span className="badge bg-primary">👁️ {popup.statistics?.view || 0}</span>
                        <span className="ms-1 text-muted">Görüntüleme</span>
                      </div>
                      <div>
                        <span className="badge bg-success">👆 {popup.statistics?.click || 0}</span>
                        <span className="ms-1 text-muted">Tıklama</span>
                      </div>
                      <div>
                        <span className="badge bg-warning">❌ {popup.statistics?.close || 0}</span>
                        <span className="ms-1 text-muted">Kapatma</span>
                      </div>
                      <div>
                        <span className="badge bg-danger">🚫 {popup.statistics?.dismiss || 0}</span>
                        <span className="ms-1 text-muted">Bir Daha Gösterme</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    {popup.is_active ? (
                      <span className="badge bg-success">Aktif</span>
                    ) : (
                      <span className="badge bg-secondary">Pasif</span>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => navigate(`/popups/edit/${popup.id}`)}
                        title="Düzenle"
                      >
                        <Icon icon="mdi:pencil" />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          setSelectedPopup(popup);
                          setShowDeleteModal(true);
                        }}
                        title="Sil"
                      >
                        <Icon icon="mdi:delete" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Toplam {pagination.total} popup, Sayfa {pagination.page} / {pagination.totalPages}
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Önceki
                </button>
              </li>
              <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Sonraki
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedPopup && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Popup Sil</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedPopup(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>"{selectedPopup.title}" popup'ını silmek istediğinize emin misiniz?</p>
                <p className="text-danger">Bu işlem geri alınamaz!</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedPopup(null);
                  }}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupsTable;

